import React, { forwardRef, useState, useCallback } from 'react';
import { Box, Typography, IconButton, Slide, Stack, Tooltip } from '@mui/material';
import { Close } from '@mui/icons-material';
import styles from './ApiRequestModal.module.scss';
import { ReactComponent as SaveIcon } from 'new/components/icons/fi-rr-disc.svg';
import { ReactComponent as CopyIcon } from 'new/components/icons/copy.svg';
import { useApiRequestModal } from '../../context/ApiRequestModal/ApiRequestModalContext';
import { ChipBadge } from 'new/elements/ChipBadge';
import { Button } from 'new/elements/Button';
import { useCreateApiRequest } from 'new/hooks/useCreateApiRequest';
import { ConfigPanel, TestPanel, RequestConfirmationDialog } from './components';
import {
  useApiRequestForm,
  useParameterManager,
  useJsonSchema,
  useDragAndResize,
  UIState,
} from './hooks';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { UPDATE_DATA_SET_INFO_REQUEST } from 'js/constants/actionTypes';

export const ApiRequestModal = forwardRef<HTMLDivElement, {}>((props, ref) => {
  const { modalState, switchMode, closeModal } = useApiRequestModal();
  const { updateRequestEndpointSpec } = useCreateApiRequest();
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const {
    formData,
    uiState,
    responseState,
    validationErrors,
    updateFormData,
    updateUIState,
    updateResponseState,
    setRawJsonBody,
    setGeneratedSchema,
    hasUnsavedChanges,
    hasValidationErrors,
    clearValidationErrors,
    resetOriginalData,
  } = useApiRequestForm(
    modalState.datasetId,
    modalState.mode,
    modalState.sourceId,
    modalState.method,
  );
  const { addParameter, addHeader, updateParameter, updateHeader, removeParameter, removeHeader } =
    useParameterManager();
  const { generateSchema: generateSchemaFromJson } = useJsonSchema();
  const { modalRef, handleMouseDown, handleResizeMouseDown } = useDragAndResize();
  const isSavingConfig = useIsLoading(UPDATE_DATA_SET_INFO_REQUEST);
  const endpointUrl = `${formData.baseUrl}${formData.route}`;

  const handleGenerateSchema = useCallback(() => {
    const schemaString = generateSchemaFromJson(formData.rawJsonBody || '{}');
    setGeneratedSchema(schemaString);
  }, [generateSchemaFromJson, formData.rawJsonBody, setGeneratedSchema]);

  const handleParameterUpdate = useCallback(
    (id: number, field: string, value: any, type: 'path' | 'query' | 'body') => {
      if (type === 'path') {
        const updated = updateParameter(formData.pathParameters, id, field as any, value);
        updateFormData({ pathParameters: updated });
      } else if (type === 'query') {
        const updated = updateParameter(formData.queryParameters, id, field as any, value);
        updateFormData({ queryParameters: updated });
      }
    },
    [formData.pathParameters, formData.queryParameters, updateParameter, updateFormData],
  );

  const handleParameterRemove = useCallback(
    (id: number, type: 'path' | 'query' | 'body') => {
      if (type === 'path') {
        const updated = removeParameter(formData.pathParameters, id);
        updateFormData({ pathParameters: updated });
      } else if (type === 'query') {
        const updated = removeParameter(formData.queryParameters, id);
        updateFormData({ queryParameters: updated });
      }
    },
    [formData.pathParameters, formData.queryParameters, removeParameter, updateFormData],
  );

  const handleParameterAdd = useCallback(
    (type: 'path' | 'query' | 'body') => {
      if (type === 'path') {
        const updated = addParameter(formData.pathParameters, type);
        updateFormData({ pathParameters: updated });
      } else if (type === 'query') {
        const updated = addParameter(formData.queryParameters, type);
        updateFormData({ queryParameters: updated });
      }
    },
    [formData.pathParameters, formData.queryParameters, addParameter, updateFormData],
  );

  const handleHeaderUpdate = useCallback(
    (id: number, field: string, value: string | boolean) => {
      const updated = updateHeader(formData.headers, id, field as any, value);
      updateFormData({ headers: updated });
    },
    [formData.headers, updateHeader, updateFormData],
  );

  const handleHeaderRemove = useCallback(
    (id: number) => {
      const updated = removeHeader(formData.headers, id);
      updateFormData({ headers: updated });
    },
    [formData.headers, removeHeader, updateFormData],
  );

  const handleHeaderAdd = useCallback(() => {
    const updated = addHeader(formData.headers);
    updateFormData({ headers: updated });
  }, [formData.headers, addHeader, updateFormData]);

  const handleToggleSection = useCallback(
    (section: keyof UIState) => {
      updateUIState({ [section]: !uiState[section] });
    },
    [uiState, updateUIState],
  );

  const handleSave = useCallback(
    (e?: React.MouseEvent) => {
      // Don't save if there are validation errors
      if (hasValidationErrors()) {
        console.log('ERROR');
        return Promise.reject(new Error('Validation errors'));
      }

      let bodyData = {};
      if (formData.rawJsonBody && formData.rawJsonBody.trim() !== '{}') {
        try {
          bodyData = JSON.parse(formData.rawJsonBody);
        } catch (error) {
          console.warn('Invalid JSON in body, saving as empty object:', error);
          bodyData = {};
        }
      }

      const config = {
        method: formData.requestMethod,
        headers: formData.headers.map((h) => ({
          key: h.key,
          value: h.value,
          required: h.required,
        })),
        path_params: formData.pathParameters.map((p) => {
          const isDynamic = p.key && p.key.startsWith('{') && p.key.endsWith('}');
          return {
            key: p.key,
            value: isDynamic ? p.value : p.key, // use key as value for static path params
            required: p.required,
          };
        }),
        query_params: formData.queryParameters.map((q) => ({
          key: q.key,
          value: q.value,
          required: q.required,
        })),
        ...(formData.requestMethod !== 'GET' && { body: bodyData }),
      };

      return updateRequestEndpointSpec(modalState.datasetId, config)
        .then(() => {
          // reset original data to current form data after saving
          resetOriginalData();
          if (e) {
            closeModal();
          }
        })
        .catch((error) => {
          console.error('Error saving API endpoint configuration:', error);
          throw error;
        });
    },
    [
      formData,
      modalState.datasetId,
      updateRequestEndpointSpec,
      closeModal,
      resetOriginalData,
      hasValidationErrors,
    ],
  );

  const handleSaveAndTest = useCallback(() => {
    handleSave()
      .then(() => {
        setShowSaveConfirmation(false);
        switchMode('test');
      })
      .catch((error) => {
        console.error('Error in save and test:', error);
        setShowSaveConfirmation(false);
      });
  }, [handleSave, switchMode]);

  const handleTestClick = useCallback(() => {
    const hasErrors = hasValidationErrors();
    const hasChanges = hasUnsavedChanges();

    if (!hasErrors && !hasChanges) {
      clearValidationErrors();
      switchMode('test');
    } else {
      setShowSaveConfirmation(true);
    }
  }, [hasValidationErrors, hasUnsavedChanges, switchMode, clearValidationErrors]);

  const handleTestRequest = useCallback(async () => {
    setIsLoading(true);
    try {
      const missingRequired = formData.pathParameters
        .filter((p) => p.required && (!p.value || p.value.trim() === ''))
        .map((p) => p.key);

      if (missingRequired.length > 0) {
        updateResponseState({
          responseStatus: 'Validation Error',
          responseData: JSON.stringify(
            {
              error: 'Missing required parameters',
              missing: missingRequired,
            },
            null,
            2,
          ),
        });
        return;
      }

      const requestConfig: RequestInit = {
        method: formData.requestMethod,
        headers: {
          'X-Nexla-Test-Mode': 'true',
        } as Record<string, string>,
      };

      formData.headers.forEach((header) => {
        if (header.key) {
          (requestConfig.headers as Record<string, string>)[header.key] = header.value || '';
        }
      });

      if (formData.rawJsonBody && formData.rawJsonBody.trim() !== '{}') {
        if (!(requestConfig.headers as Record<string, string>)['Content-Type']) {
          (requestConfig.headers as Record<string, string>)['Content-Type'] = 'application/json';
        }
        requestConfig.body = formData.rawJsonBody;
      }

      let fullUrl = endpointUrl;

      // path param replacement for macros
      formData.pathParameters.forEach((param) => {
        if (param.key && param.value) {
          if (param.key.startsWith('{') && param.key.endsWith('}')) {
            if (fullUrl.includes(param.key)) {
              fullUrl = fullUrl.replace(param.key, encodeURIComponent(param.value));
            }
          }
        }
      });

      const queryParams = new URLSearchParams();

      if (formData.apiKey) {
        queryParams.append('api_key', formData.apiKey);
      }

      formData.queryParameters.forEach((param) => {
        if (param.key && param.value) {
          queryParams.append(param.key, param.value);
        }
      });

      if (queryParams.toString()) {
        fullUrl += `?${queryParams.toString()}`;
      }

      const response = await fetch(fullUrl, requestConfig);
      const statusText = `${response.status} ${response.statusText}`;

      // convert response headers to a plain object
      const responseHeaders: { [key: string]: string } = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      let responseBody: string;
      try {
        const jsonResponse = await response.json();
        responseBody = JSON.stringify(jsonResponse, null, 2);
      } catch {
        try {
          responseBody = await response.text();
        } catch {
          responseBody = `Unable to parse response (${response.status} ${response.statusText})`;
        }
      }

      updateResponseState({
        responseStatus: statusText,
        responseData: responseBody,
        responseHeaders,
      });
    } catch (error) {
      let errorMessage = 'Request failed';
      let errorDetails = 'Unknown error';

      if (error instanceof Error) {
        errorDetails = error.message;
      }

      updateResponseState({
        responseStatus: 'Error',
        responseData: JSON.stringify(
          {
            error: errorMessage,
            message: errorDetails,
          },
          null,
          2,
        ),
      });
    } finally {
      setIsLoading(false);
    }
  }, [formData, updateResponseState]);

  const handleCloseClick = useCallback(() => {
    if (isSavingConfig) {
      return; // Prevent closing while saving
    }
    if (modalState.mode === 'config' && hasUnsavedChanges()) {
      setShowCloseConfirmation(true);
    } else {
      closeModal();
    }
  }, [modalState.mode, hasUnsavedChanges, closeModal, isSavingConfig]);

  const handleSaveAndClose = useCallback(() => {
    handleSave();
    setShowCloseConfirmation(false);
    closeModal();
  }, [handleSave, closeModal]);

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) {
        handleCloseClick();
      }
    },
    [handleCloseClick],
  );

  if (!modalState.isOpen) return null;

  return (
    <Slide direction="left" in={Boolean(modalState.isOpen)} unmountOnExit>
      <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
        <Box
          ref={modalRef}
          className={`${styles.modalContainer} ${styles[modalState.mode + 'Mode']}`}
        >
          <Box className={styles.header} onMouseDown={handleMouseDown}>
            <Typography variant="caption">
              {modalState.mode === 'config'
                ? 'Configure Request Endpoint'
                : 'Test Request Response'}
            </Typography>
            <Stack direction="row" gap="12px" alignItems="center">
              {modalState.mode === 'config' && (
                <Tooltip
                  title={hasValidationErrors() ? 'Please fix validation errors before saving' : ''}
                  placement="bottom"
                >
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={
                        isSavingConfig ? (
                          <div className={styles.spinner} />
                        ) : (
                          <SaveIcon
                            className={hasValidationErrors() ? styles.disabledIcon : undefined}
                          />
                        )
                      }
                      onClick={handleSave}
                      disabled={hasValidationErrors() || isSavingConfig}
                    >
                      {isSavingConfig ? 'Saving...' : 'Save'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <IconButton onClick={handleCloseClick} size="small">
                <Close />
              </IconButton>
            </Stack>
          </Box>
          <Box className={styles.content}>
            <div className={styles.modalContent}>
              <div className={styles.endpointSection}>
                <ChipBadge label={formData.requestMethod} color="primary" variant="outlined" />
                <div className={styles.endpointContainer}>
                  <Tooltip title={endpointUrl} placement="bottom">
                    <Typography
                      variant="bodySmall"
                      fontFamily="monospace"
                      className={styles.endpointUrl}
                    >
                      {endpointUrl}
                    </Typography>
                  </Tooltip>
                  <Tooltip title="Copy URL">
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(endpointUrl);
                      }}
                      className={styles.copyButton}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </div>
                {modalState.mode === 'test' ? (
                  <Button variant="contained" onClick={handleTestRequest} disabled={isLoading}>
                    {isLoading ? 'Sending...' : 'Send'}
                  </Button>
                ) : (
                  <Button variant="outlined" onClick={handleTestClick}>
                    Test
                  </Button>
                )}
              </div>
              <div
                className={`${styles.mainView} ${modalState.mode === 'test' ? styles.splitView : ''}`}
              >
                {modalState.mode === 'config' ? (
                  <ConfigPanel
                    mode={modalState.mode}
                    pathParameters={formData.pathParameters}
                    queryParameters={formData.queryParameters}
                    headers={formData.headers}
                    rawJsonBody={formData.rawJsonBody}
                    generatedSchema={formData.generatedSchema}
                    method={formData.requestMethod}
                    uiState={uiState}
                    validationErrors={validationErrors}
                    onParameterUpdate={handleParameterUpdate}
                    onParameterRemove={handleParameterRemove}
                    onParameterAdd={handleParameterAdd}
                    onHeaderUpdate={handleHeaderUpdate}
                    onHeaderRemove={handleHeaderRemove}
                    onHeaderAdd={handleHeaderAdd}
                    onToggleSection={handleToggleSection}
                    onJsonBodyChange={setRawJsonBody}
                    onGenerateSchema={handleGenerateSchema}
                  />
                ) : (
                  <TestPanel
                    mode={modalState.mode}
                    pathParameters={formData.pathParameters}
                    queryParameters={formData.queryParameters}
                    headers={formData.headers}
                    rawJsonBody={formData.rawJsonBody}
                    method={formData.requestMethod}
                    uiState={uiState}
                    responseState={responseState}
                    validationErrors={validationErrors}
                    onParameterUpdate={handleParameterUpdate}
                    onParameterRemove={handleParameterRemove}
                    onParameterAdd={handleParameterAdd}
                    onHeaderUpdate={handleHeaderUpdate}
                    onHeaderRemove={handleHeaderRemove}
                    onHeaderAdd={handleHeaderAdd}
                    onToggleSection={handleToggleSection}
                    onJsonBodyChange={setRawJsonBody}
                    onResponseTabChange={(tab) => updateResponseState({ responseTab: tab })}
                  />
                )}
              </div>
            </div>
          </Box>
          <Box className={styles.footer}>
            <Box className={styles.resizeHandle} onMouseDown={handleResizeMouseDown} />
          </Box>
          {showSaveConfirmation && (
            <RequestConfirmationDialog
              type="save"
              hasErrors={hasValidationErrors()}
              hasChanges={hasUnsavedChanges()}
              onSaveAndTest={handleSaveAndTest}
              onSaveAndClose={handleSaveAndClose}
              onCloseModal={closeModal}
              onDismiss={() => setShowSaveConfirmation(false)}
            />
          )}
          {showCloseConfirmation && (
            <RequestConfirmationDialog
              type="close"
              hasErrors={hasValidationErrors()}
              hasChanges={hasUnsavedChanges()}
              onSaveAndTest={handleSaveAndTest}
              onSaveAndClose={handleSaveAndClose}
              onCloseModal={closeModal}
              onDismiss={() => setShowCloseConfirmation(false)}
            />
          )}
        </Box>
      </div>
    </Slide>
  );
});
